import {
  countTotalProductsPerAvailability,
  IProductListPatchModel,
} from '@epicuro-next/data/lists';
import { List } from '@epicuro-next/models/engagement';
import {
  getListFeedIdPropertyByFeedType,
  isBefore,
} from '@epicuro-next/utilities';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import * as actions from '../../actions';

/**
 * Product List feature key
 */
export const productListsFeatureKey = 'lists';
/**
 * ProductList State
 */
export interface State extends EntityState<List> {
  /** Product lists total count(on a server) */
  total: number;

  /**
   * the result page to fetch
   */
  page: number;

  /**
   * Page size
   */
  size: number;

  /** loading indicator */
  loading: boolean;
}

/** State adapter */
export const adapter: EntityAdapter<List> = createEntityAdapter<List>({
  sortComparer: listComparer,
});

/**
 * Initial state for ProductList
 */
export const initialState: State = adapter.getInitialState({
  total: 0,
  page: 1,
  size: 10,
  loading: false,
});

/**
 * ProductList Reducer
 */
const productListReducer = createReducer(
  initialState,
  on(actions.resetProductListsAdapter, () => initialState),

  on(actions.loadProductListsSuccess, (state, action) => {
    return adapter.upsertMany(action.items, {
      ...state,
      total: action.total,
      page: state.page + 1,
      loading: false,
    });
  }),

  on(actions.loadProductLists, (state) => ({
    ...state,
    loading: true,
  })),

  on(actions.loadProductListsFailure, (state) => {
    return adapter.removeAll({
      ...state,
      loading: false,
    });
  }),

  on(actions.loadProductListSuccess, (state, { list, oldList }) => {
    // list will contains less products then old list after optimistic update
    if (oldList) {
      const idsAreEqual = oldList.id === list.id;
      const newContainsLess =
        (list.productsReference?.length ?? 0) <
        (oldList.productsReference?.length ?? 0);
      if (idsAreEqual && newContainsLess) {
        return state;
      }
    }
    return adapter.upsertOne(list, { ...state });
  }),

  on(actions.bulkDeleteListsSuccessFinalize, (state, { ids }) => {
    return adapter.removeMany(ids, {
      ...state,
      total: state.total - ids.length,
    });
  }),

  on(
    actions.updateProductListSuccess,
    actions.updateCommercialListSuccess,
    (state, action) => {
      if (action?.isEditedByOtherAdminWithPermission) return state;

      return adapter.upsertOne(action.list, {
        ...state,
      });
    },
  ),

  on(actions.cloneProductListSuccess, (state) => ({
    ...state,
    total: state.total + 1,
  })),

  on(actions.createListSuccess, (state, action) => {
    const upsert = [action.list];

    const entries = Object.values(state.entities) as List[];
    const active: List | undefined = entries.find((item) => item?.isActive);

    if (active) {
      upsert.push({
        ...active,
        isActive: false,
      });
    }
    return adapter.upsertMany(upsert, {
      ...state,
      total: state.total + 1,
    });
  }),

  on(actions.createCommercialListSuccess, (state, action) => {
    return adapter.upsertOne(action.list, {
      ...state,
      total: state.total + 1,
    });
  }),

  on(actions.setActiveList, (state, action) => {
    const entries = Object.values(state.entities) as List[];

    const nextActive: List | undefined = entries.find(
      (item) => item?.id === action.id,
    );

    if (!nextActive) {
      return state;
    }
    const active: List | undefined = entries.find((item) => item?.isActive);
    return adapter.updateMany(
      [
        {
          id: nextActive.id,
          changes: {
            isActive: true,
          },
        },
        {
          id: active?.id ?? '', // won't do anything with empty str
          changes: {
            isActive: false,
          },
        },
      ],
      state,
    );
  }),

  on(actions.setActiveListFail, (state) => {
    const active: List | undefined = Object.values(state.entities).find(
      (item) => item?.isActive,
    );

    if (active) {
      return adapter.upsertOne(
        {
          ...active,
          isActive: false,
        },
        state,
      );
    }

    return state;
  }),

  on(actions.addProductsToList, (state, { list, products }) => {
    const active: List =
      Object.values(state.entities).find((item) => item?.isActive) ?? list;

    const productsAvailabilityTotal =
      countTotalProductsPerAvailability(products);

    if (active) {
      const productsReference = [
        ...products.map((p) => ({
          productId: p.productId,
          brandId: p.brandId,
        })),
        ...(active.productsReference ?? []),
      ];

      return adapter.upsertOne(
        {
          ...active,
          productsReference,
          availableProductsCount:
            (active.availableProductsCount ?? 0) +
            productsAvailabilityTotal.available,
          unavailableProductsCount:
            (active.unavailableProductsCount ?? 0) +
            productsAvailabilityTotal.unavailable,
          amount: (active.amount ?? 0) + products.length,
        },
        state,
      );
    }

    return state;
  }),

  on(actions.deleteProductsFromList, (state, { list, products }) => {
    const listInState: List | undefined = Object.values(state.entities).find(
      (item) => list.id === item?.id,
    );

    const newAvailable =
      (listInState?.availableProductsCount ?? 0) - products.length;

    const newUnavailable = listInState?.unavailableProductsCount ?? 0;

    if (listInState) {
      return adapter.upsertOne(
        {
          ...listInState,
          productsReference: [
            ...(listInState.productsReference ?? []).filter(
              (productReference) =>
                !products.some(
                  ({ productId, brandId }) =>
                    productId === productReference.productId &&
                    brandId === productReference.brandId,
                ),
            ),
          ],
          amount: listInState.amount ? listInState.amount - products.length : 0,
          availableProductsCount: newAvailable,
          unavailableProductsCount: newUnavailable,
        },
        state,
      );
    }

    return state;
  }),

  on(
    actions.exportListToEmailSuccess,
    (state, { emailData, productListId }) => {
      const list: List | undefined = Object.values(state.entities).find(
        (item) => item?.id === productListId,
      );

      if (list) {
        const newRecipients = (list.recipients?.emails ?? []).concat(
          (emailData?.recipients ?? []).reduce(
            (acc, { email }) => [...acc, email],
            [],
          ),
        );

        return adapter.upsertOne(
          {
            ...list,
            recipients: {
              count: newRecipients.length,
              emails: newRecipients,
            },
          },
          state,
        );
      }

      return state;
    },
  ),

  // Revert unsuccessfull action of patch request
  on(actions.patchProductsBulkFailed, (state, { products }) => {
    const productsMap = new Map<string, IProductListPatchModel>(
      products.map(({ listId, patchModel }) => [listId, patchModel]),
    );
    return adapter.upsertMany(
      Object.values(state.entities)
        .map((list: List): List | undefined => {
          const listFailedToPatchModel: IProductListPatchModel | undefined =
            productsMap.get(list?.id);
          if (listFailedToPatchModel) {
            return {
              ...list,
              productsReference:
                list.productsReference
                  ?.filter(
                    (productReference) =>
                      !listFailedToPatchModel.itemsToAdd.some(
                        ({ productId, brandId }) =>
                          productId === productReference.productId &&
                          brandId === productReference.brandId,
                      ),
                  )
                  .concat(listFailedToPatchModel.itemsToDelete) ?? [],
              amount: (list.amount ?? 0) + products.length,
            };
          }

          return undefined;
        })
        .filter((v: List | undefined): v is List => !!v),
      state,
    );
  }),

  on(actions.updateListNewsFeedId, (state, action) => {
    return adapter.updateOne(
      {
        id: action.listId as string,
        changes: {
          [getListFeedIdPropertyByFeedType(action.newsfeedType)]:
            action.newsfeedId,
        },
      },
      state,
    );
  }),
  on(
    actions.reorderProductInOpenedListSuccess,
    (state, { listId, productId, order }) => {
      const listEntity = state.entities[listId];

      const productsReference = Array.from(listEntity?.productsReference ?? []);

      if (productsReference.length === 0) return state;

      const reOrderedProductIndex = productsReference.findIndex(
        (reference) => reference.productId === productId,
      );

      const removedProduct = productsReference.splice(
        reOrderedProductIndex,
        1,
      )[0];

      productsReference.splice(order - 1, 0, removedProduct);

      return adapter.updateOne(
        {
          id: listId,
          changes: {
            productsReference,
          },
        },
        state,
      );
    },
  ),
  on(actions.resetProductListStore, () => initialState),
);

/**
 * Reducer export
 * @param state the State
 * @param action the Action emitted
 */
export function reducer(state: State | undefined, action: Action) {
  return productListReducer(state, action);
}

function listComparer(a: List, b: List) {
  if (a.isActive) return -1;
  if (b.isActive) return 1;
  return isBefore(
    new Date(a.update.dateUtc ?? a.creation.dateUtc),
    new Date(b.update.dateUtc ?? b.creation.dateUtc),
  )
    ? 1
    : 0;
}
